import React, { useState, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import axios from "axios";
import "../styles/GlobalStyles.css";

/* ================= REQUIRED FIELDS PER SECTION ================= */

const requiredFieldsBySection = {
  "10th": ["board", "school", "country", "city", "year", "grading", "grade"],
  "12th": [
    "board",
    "school",
    "country",
    "city",
    "year",
    "grading",
    "grade",
    "stream",
  ],
  graduation: [
    "specialization",
    "institution",
    "fromYear",
    "toYear",
    "grading",
    "grade",
    "courseType",
  ],
  masters: [
    "specialization",
    "institution",
    "fromYear",
    "toYear",
    "grading",
    "grade",
    "courseType",
  ],
  phd: [
    "specialization",
    "institution",
    "fromYear",
    "toYear",
    "grading",
    "grade",
    "courseType",
  ],
};

const MIN_DURATION = {
  "12th": 2,        // 11th + 12th
  graduation: 3,
  masters: 2,
  phd: 3,
};


const EducationSection = ({ onSuccess }) => {
  const [openSection, setOpenSection] = useState(null);
  const [savedData, setSavedData] = useState({});
const [gradeError, setGradeError] = useState("");


  const initialForm = {
    board: "",
    school: "",
    country: "India",
    city: "",
    year: "",
    stream: "",
    grading: "",
    grade: "",
    specialization: "",
    institution: "",
    fromYear: "",
    toYear: "",
    courseType: "",
  };
  const specializations = [
    "NTT (Nursery Teacher Training)",
    "D.El.Ed (Diploma in Elementary Education)",
    "B.Ed (Bachelor of Education)",
    "Integrated Teacher Education Program (ITEP)",
    "TET / CTET",
    "NET",
    "SET",
    "MA",
    "MSc",
    "MCom",
    "MCA",
    "MSW",
    "PhD",
  ];
const getYear = (key, type = "end") => {
  const data = savedData[key];
  if (!data) return null;

  if (data.year_of_passing) {
    return parseInt(data.year_of_passing);
  }

  if (type === "start" && data.start_date) {
    return new Date(data.start_date).getFullYear();
  }

  if (type === "end" && data.end_date) {
    return new Date(data.end_date).getFullYear();
  }

  return null;
};
const getMinAllowedYear = (sectionKey, type = "start") => {
  switch (sectionKey) {
    case "12th": {
      const tenth = getYear("10th");
      return tenth ? tenth + 2 : null;
    }

    case "graduation": {
      const twelfth = getYear("12th");
      return twelfth ? twelfth + 1 : null;
    }

    case "masters": {
      const gradEnd = getYear("graduation", "end");
      return gradEnd ? gradEnd + 1 : null;
    }

    case "phd": {
      const mastersEnd = getYear("masters", "end");
      return mastersEnd ? mastersEnd + 1 : null;
    }

    default:
      return null; // 10th
  }
};

  const [form, setForm] = useState(initialForm);

  /* ================= LOAD EDUCATION ================= */
  useEffect(() => {
    const fetchEducation = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/profile/getCandprofile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const edu = res.data?.candidate?.education || [];
        const mapped = {};

        edu.forEach((e) => {
          if (e.education) mapped[e.education] = e;
        });

        setSavedData(mapped);
      } catch (err) {
        console.error("Failed to load education", err);
      }
    };

    fetchEducation();
  }, []);

  /* ================= HANDLERS ================= */

const toggleSection = (key) => {
  const isClosing = openSection === key;

  setOpenSection(isClosing ? null : key);
  setForm(!isClosing && savedData[key]
    ? mapBackendToForm(savedData[key])
    : initialForm
  );
};



 const handleChange = (e) => {
  const { name, value } = e.target;

  // Handle grading system change
  if (name === "grading") {
    setForm({ ...form, grading: value, grade: "" });
    setGradeError("");
    return;
  }

  // Handle From / To Year validation
  if (name === "fromYear" || name === "toYear") {
  setForm({ ...form, [name]: value });
  return;
}


  // Handle Grade field dynamically
  if (name === "grade") {
    let newValue = value;
    setGradeError("");

    if (form.grading === "Percentage") {
      newValue = newValue.replace(/[^0-9.]/g, "");
      if ((newValue.match(/\./g) || []).length > 1) return;

      if (newValue.includes(".")) {
        const [, decimals] = newValue.split(".");
        if (decimals.length > 3) return;
      }

      if (parseFloat(newValue) > 100) {
        setGradeError("Percentage cannot exceed 100.");
        return;
      }
    }

    if (form.grading === "CGPA") {
      newValue = newValue.replace(/[^0-9.]/g, "");
      if ((newValue.match(/\./g) || []).length > 1) return;

      if (newValue.includes(".")) {
        const [, decimals] = newValue.split(".");
        if (decimals.length > 2) return;
      }

      if (parseFloat(newValue) > 10) {
        setGradeError("CGPA cannot exceed 10.");
        return;
      }
    }

    if (form.grading === "Grade") {
      newValue = newValue.toUpperCase().replace(/[^ABC]/g, "");
    }

    setForm({ ...form, grade: newValue });
    return;
  }

  // Normal fields
  setForm({ ...form, [name]: value });
};




  const isFormValid = (sectionKey) => {
    return requiredFieldsBySection[sectionKey].every((f) => form[f]);
  };
const getYearOptions = (sectionKey, type = "start") => {
  const minYear = getMinAllowedYear(sectionKey);
  let filtered = years;

  // Enforce academic progression
  if (minYear) {
    filtered = filtered.filter(y => parseInt(y) >= minYear);
  }

  // Enforce minimum duration inside same course
  if (type === "end" && form.fromYear) {
    const minDuration = MIN_DURATION[sectionKey] || 1;
    const minEndYear = parseInt(form.fromYear) + minDuration - 1;

    filtered = filtered.filter(y => parseInt(y) >= minEndYear);
  }

  return filtered;
};


  /* ================= FRONTEND → BACKEND MAPPING ================= */

  const mapFormToBackend = (key, form) => {
    if (key === "10th" || key === "12th") {
      return {
        education: key,
        board: form.board,
        stream: key === "12th" ? form.stream : undefined,
        institution_name: form.school,
        country: form.country,
        city: form.city,
        year_of_passing: form.year,
        grading_system: form.grading,
        grade: form.grade,
      };
    }

    return {
      education: key,
      field_of_study: form.specialization,
      institution_name: form.institution,
      start_date: new Date(`${form.fromYear}-01-01`),
      end_date: new Date(`${form.toYear}-01-01`),
      grading_system: form.grading,
      grade: form.grade,
      course_type: form.courseType,
    };
  };

  const mapBackendToForm = (data) => ({
    board: data.board || "",
    school: data.institution_name || "",
    country: data.country || "India",
    city: data.city || "",
    year: data.year_of_passing || "",
    stream: data.stream || "",
    grading: data.grading_system || "",
    grade: data.grade || "",
    specialization: data.field_of_study || "",
    institution: data.institution_name || "",
    fromYear: data.start_date
      ? new Date(data.start_date).getFullYear().toString()
      : "",
    toYear: data.end_date
      ? new Date(data.end_date).getFullYear().toString()
      : "",
    courseType: data.course_type || "",
  });

const saveSection = async (key) => {
  if (!isFormValid(key)) {
    alert("All fields are required");
    return;
  }


  const year = parseInt(form.year);
  
const currentYear = new Date().getFullYear();

if ((key === "10th" || key === "12th") && year > currentYear) {
  alert("Year of passing cannot be in the future");
  return;
}

  
  /* ===== SAVE ===== */
  try {
    const token = localStorage.getItem("token");
    if (!token) return alert("Please login");

    const payload = mapFormToBackend(key, form);

    await axios.put(
      `${process.env.REACT_APP_API_URL}/api/profile/update/education`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setSavedData({ ...savedData, [key]: payload });
    setOpenSection(null);
    onSuccess && onSuccess();
  } catch {
    alert("Failed to save education details");
  }
};



  /* ================= UI ================= */

  return (
    <div className="profile-card">
      <h2 className="page-title">Education Details</h2>

      <div className="education-info">
        Research says that profiles with education details stand out to recruiters
      </div>

      {sections.map(({ key, label, type }) => (
        <div key={key} className="detail-card">
          <div className="education-card-header" onClick={() => toggleSection(key)}>
            <span>{label}</span>
            {openSection === key ? <FaChevronUp /> : <FaChevronDown />}
          </div>



          {openSection === key && (
            <div className="education-form">
              {type === "school" && (
                <>
                  <SelectField label="Board *" name="board" value={form.board} onChange={handleChange} options={boards} />
                  <InputField label="School Name *" name="school" value={form.school} onChange={handleChange} />

                  <div className="two-col">
                    <SelectField label="Country *" name="country" value={form.country} onChange={handleChange} options={countries} />
                    <InputField label="City *" name="city" value={form.city} onChange={handleChange} />
                  </div>

                  <div className="two-col">
                    <SelectField label="Year of Passing *" name="year" value={form.year} onChange={handleChange} options={getYearOptions(key)}
 />
                    <SelectField label="Grading System *" name="grading" value={form.grading} onChange={handleChange} options={gradingSystems} />
                  </div>

                  {key === "12th" && (
                    <SelectField label="Stream *" name="stream" value={form.stream} onChange={handleChange} options={streams} />
                  )}

                  <InputField label="Grade *" name="grade" value={form.grade} onChange={handleChange} />
                </>
              )}

              {type === "degree" && (
                <>
                  <SelectField
                    label="Specialization *"
                    name="specialization"
                    value={form.specialization}
                    onChange={handleChange}
                    options={specializations}
                  />

                  <InputField label="Institution Name *" name="institution" value={form.institution} onChange={handleChange} />

                 <div className="two-col">
 <SelectField
  label="From Year *"
  name="fromYear"
  value={form.fromYear}
  onChange={handleChange}
  options={getYearOptions(key, "start")}
/>



  <SelectField
  label="To Year *"
  name="toYear"
  value={form.toYear}
  onChange={handleChange}
  options={getYearOptions(key, "end")}
/>


</div>




                  <div className="two-col">
                    <SelectField label="Grading System *" name="grading" value={form.grading} onChange={handleChange} options={gradingSystems} />
                    <div className="form-group">
  <label className="profile-label">
    {form.grading === "Percentage" && "Percentage *"}
    {form.grading === "CGPA" && "CGPA (Out of 10) *"}
    {form.grading === "Grade" && "Grade (A / B / C) *"}
    {!form.grading && "Grade *"}
  </label>

  <input
    className="profile-input"
    name="grade"
    placeholder={
      form.grading === "Percentage"
        ? "Enter percentage (max 100)"
        : form.grading === "CGPA"
        ? "Enter CGPA (max 10)"
        : form.grading === "Grade"
        ? "Enter grade (A / B / C)"
        : "Enter grade"
    }
    value={form.grade}
    onChange={handleChange}
    style={{
      borderColor: gradeError ? "#dc2626" : "#d1d5db",
      backgroundColor: gradeError ? "#fef2f2" : "white",
    }}
  />

  {gradeError && (
    <div style={{ color: "#dc2626", fontSize: "0.85rem", marginTop: "4px" }}>
      {gradeError}
    </div>
  )}
</div>

                  </div>

                  <SelectField label="Course Type *" name="courseType" value={form.courseType} onChange={handleChange} options={courseTypes} />
                </>
              )}

              <button className="save-button" onClick={() => saveSection(key)}>
                Save
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ================= DATA ================= */

const sections = [
  { key: "10th", label: "Class 10th", type: "school" },
  { key: "12th", label: "Class 12th", type: "school" },
  { key: "graduation", label: "Graduation / Diploma", type: "degree" },
  { key: "masters", label: "Masters / Post-Graduation", type: "degree" },
  { key: "phd", label: "PhD / Doctorate", type: "degree" },
];

const boards = ["CBSE", "ICSE", "State Board", "IB", "Other"];
const countries = ["India", "USA", "UK", "Other"];
const gradingSystems = ["Percentage", "CGPA", "Grade"];
const streams = ["Science", "Commerce", "Arts"];
const courseTypes = ["Full Time", "Part Time", "Distance"];
const years = Array.from({ length: 35 }, (_, i) => `${2025 - i}`);

/* ================= REUSABLE ================= */

const InputField = ({ label, ...props }) => (
  <div className="form-group">
    <label className="profile-label">{label}</label>
    <input className="profile-input" {...props} />
  </div>
);

const SelectField = ({ label, options, ...props }) => (
  <div className="form-group">
    <label className="profile-label">{label}</label>
    <select className="profile-input" {...props}>
      <option value="">Select</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

export default EducationSection;
